/**
 *
 *  Organizations/Show/Settings/Lures
 *
 */

import React from 'react'

// NPM Libraries
import Toggle from 'react-toggle'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// GraphQL
import useQuery from 'components/UseQuery'
import { gql, useMutation } from '@apollo/client'

import { ToggleContainer } from 'components/Organizations/Show/Integrations'

// Utils
import { Lure } from 'globalVars'
import { COLORS } from 'global-styles'

const GET_ORGANIZATION = gql`
	query organization($id: ID) {
		organization(id: $id) {
			id
			custom_crafted_lures_defaults
			ready_made_lures_defaults
		}
	}
`

const UPDATE_ORGANIZATION = gql`
	mutation updateOrganization($id: ID!, $custom_crafted_lures_defaults: JSON, $ready_made_lures_defaults: JSON) {
		updateOrganization(
			input: {
				id: $id
				custom_crafted_lures_defaults: $custom_crafted_lures_defaults
				ready_made_lures_defaults: $ready_made_lures_defaults
			}
		) {
			organization {
				id
			}
		}
	}
`

const channels = [
	{
		index: Lure.CHANNEL_DIRECT_MAIL,
		string: 'Direct Mail',
	},
	{
		index: Lure.CHANNEL_RADIO,
		string: 'Radio',
	},
	{
		index: Lure.CHANNEL_DIGITAL,
		string: 'Digital',
	},
	{
		index: Lure.CHANNEL_EMAIL,
		string: 'Email',
	},
	{
		index: Lure.CHANNEL_USPS_INFORMED_DELIVERY,
		string: 'USPS Informed Delivery',
	},
]

function Lures({ organization_id }) {
	const {
		data: {
			organization: { custom_crafted_lures_defaults, ready_made_lures_defaults },
		},
		refetch,
		loading,
	} = useQuery(GET_ORGANIZATION, {
		default_data: { organization: {} },
		variables: { id: organization_id },
		skip: !organization_id,
		fetchPolicy: 'network-only',
	})

	const [updateOrganization, { loading: updating }] = useMutation(UPDATE_ORGANIZATION, {
		onCompleted: () => refetch(),
	})

	return (
		<div style={{ marginTop: '10px' }}>
			{(loading || updating) && (
				<FontAwesomeIcon
					className="loading-spinner text-primary"
					icon="spinner"
					spin
					style={{ fontSize: '20px' }}
				/>
			)}

			<p>
				A Lure is a hyperlink, phone number, or tracking code that registrants use to respond. Each marketing
				channel has a unique Lure (Direct Mail, Digital, Email, Radio). If the Organization does not wish to
				allow specific marketing channels, use the switches below to make them "inactive".
			</p>

			<div className="row">
				<div className="col">
					{custom_crafted_lures_defaults && (
						<div className="border border-bottom-0">
							<div
								className="px-3 py-2 border-bottom text-center"
								style={{ backgroundColor: COLORS.borderGrey }}
							>
								<b>Custom Crafted Campaigns</b>
							</div>

							{channels.map((lure, index) => (
								<div key={index} className="d-flex justify-content-between px-3 py-2 border-bottom">
									<span>{lure.string}</span>

									<ToggleContainer>
										<Toggle
											checked={custom_crafted_lures_defaults[lure.index]}
											onChange={() =>
												updateOrganization({
													variables: {
														id: organization_id,
														custom_crafted_lures_defaults: {
															...custom_crafted_lures_defaults,
															[lure.index]: !custom_crafted_lures_defaults[lure.index],
														},
													},
												})
											}
											disabled={false}
										/>
									</ToggleContainer>
								</div>
							))}
						</div>
					)}
				</div>
				<div className="col">
					{ready_made_lures_defaults && (
						<div className="border border-bottom-0">
							<div
								className="px-3 py-2 border-bottom text-center"
								style={{ backgroundColor: COLORS.borderGrey }}
							>
								<b>Ready Made (567 Topics) Campaigns</b>
							</div>

							{channels.map((lure, index) => (
								<div key={index} className="d-flex justify-content-between px-3 py-2 border-bottom">
									<span>{lure.string}</span>

									<ToggleContainer>
										<Toggle
											checked={ready_made_lures_defaults[lure.index]}
											onChange={() =>
												updateOrganization({
													variables: {
														id: organization_id,
														ready_made_lures_defaults: {
															...ready_made_lures_defaults,
															[lure.index]: !ready_made_lures_defaults[lure.index],
														},
													},
												})
											}
											disabled={false}
										/>
									</ToggleContainer>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

Lures.propTypes = {
	organization_id: PropTypes.string,
}

export default Lures
